import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { regions } from '@/mocks/regions';

const gradeOptions = ['초1', '초2', '초3', '초4', '초5', '초6', '중1', '중2', '중3', '고1', '고2', '고3', '재수/N수'];

export default function MyProfile() {
  const navigate = useNavigate();
  const { profile, user, refreshProfile } = useAuth();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [academyName, setAcademyName] = useState('');
  const [grade, setGrade] = useState('');

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const isOwner = profile?.role === 'owner';
  const isStudent = profile?.role === 'student';

  useEffect(() => {
    if (!profile) return;
    setFullName(profile.full_name || '');
    setPhone(profile.phone || '');
    setCity(profile.city || '');
    setDistrict(profile.district || '');
    setAcademyName(profile.academy_name || '');
    setGrade(profile.grade || '');
  }, [profile]);

  const selectedRegion = regions.find((r) => r.city === city);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!fullName.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }
    if (!phone.trim()) {
      setError('휴대폰 번호를 입력해주세요.');
      return;
    }
    if (!/^[0-9]{10,11}$/.test(phone.replace(/[^0-9]/g, ''))) {
      setError('올바른 휴대폰 번호를 입력해주세요.');
      return;
    }
    if (!profile) return;

    const updates: {
      full_name: string;
      phone: string;
      city: string;
      district: string;
      address: string;
      academy_name?: string;
      grade?: string | null;
    } = {
      full_name: fullName.trim(),
      phone: phone.trim(),
      city,
      district,
      address: city && district ? `${city} ${district}` : profile.address || '',
    };

    if (isStudent || isOwner) {
      updates.academy_name = academyName.trim();
    }
    if (isStudent) {
      updates.grade = grade || null;
    }

    setSaving(true);
    const { error: dbErr } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', profile.id);
    setSaving(false);

    if (dbErr) {
      setError('저장 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    // 로그인 시 표시되는 이름도 함께 갱신
    await supabase.auth.updateUser({
      data: { full_name: fullName.trim() },
    });

    await refreshProfile();
    setMessage('내 정보가 저장되었습니다.');
  };

  return (
    <div className="min-h-screen bg-background-50">
      <header className="bg-white border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-foreground-600 hover:text-foreground-900 text-sm transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            대시보드로 돌아가기
          </button>
          <div className="text-sm font-semibold text-foreground-950">내 정보 수정</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        <div className="space-y-6">
          <section className="bg-white rounded-2xl border border-background-200 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className="ri-user-settings-line text-xl"></i>
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-foreground-950">기본 정보</h1>
                <p className="text-sm text-foreground-500">계정 정보를 수정할 수 있어요</p>
              </div>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <Field label="이메일">
                <input
                  type="email"
                  value={user?.email ?? ''}
                  disabled
                  className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-500 cursor-not-allowed"
                />
                <span className="block text-xs text-foreground-400 mt-1">
                  이메일은 변경할 수 없어요.
                </span>
              </Field>

              <Field label="이름">
                <input
                  type="text"
                  name="full_name"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  placeholder="이름"
                />
              </Field>

              <Field label="휴대폰 번호">
                <input
                  type="tel"
                  name="phone"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  placeholder="010-1234-5678"
                />
              </Field>

              {(isStudent || isOwner) && (
                <Field label="학원명">
                  <input
                    type="text"
                    name="academy_name"
                    value={academyName}
                    onChange={(e) => setAcademyName(e.target.value)}
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                    placeholder={isStudent ? '재학 중인 학원 이름' : '운영 중인 학원 이름'}
                  />
                </Field>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="시/도">
                  <select
                    name="city"
                    value={city}
                    onChange={(e) => {
                      setCity(e.target.value);
                      setDistrict('');
                    }}
                    className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                  >
                    <option value="">시/도 선택</option>
                    {regions.map((r) => (
                      <option key={r.city} value={r.city}>
                        {r.city}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="구/군">
                  <select
                    name="district"
                    value={district}
                    onChange={(e) => setDistrict(e.target.value)}
                    disabled={!city}
                    className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">구/군 선택</option>
                    {selectedRegion?.districts.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              {isStudent && (
                <Field label="학년">
                  <select
                    name="grade"
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                  >
                    <option value="">학년 선택</option>
                    {gradeOptions.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {error}
                </div>
              )}
              {message && (
                <div className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={saving}
                className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-2.5 rounded-md transition-colors cursor-pointer disabled:opacity-60 text-sm"
              >
                {saving ? '저장 중...' : '저장하기'}
              </button>
            </form>
          </section>

          <PasswordSection />
        </div>
      </main>
    </div>
  );
}

function PasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword.length < 6) {
      setError('새 비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    if (newPassword !== confirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    setLoading(true);

    // 현재 비밀번호로 로그인 재확인
    const { data: userData } = await supabase.auth.getUser();
    const email = userData.user?.email;
    if (!email) {
      setLoading(false);
      setError('계정 정보를 확인할 수 없습니다. 다시 로그인해주세요.');
      return;
    }

    const { error: signInErr } = await supabase.auth.signInWithPassword({
      email,
      password: currentPassword,
    });
    if (signInErr) {
      setLoading(false);
      setError('현재 비밀번호가 올바르지 않습니다.');
      return;
    }

    const { error: updateErr } = await supabase.auth.updateUser({ password: newPassword });
    setLoading(false);

    if (updateErr) {
      setError('비밀번호 변경에 실패했습니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    setCurrentPassword('');
    setNewPassword('');
    setConfirm('');
    setMessage('비밀번호가 변경되었습니다.');
  };

  return (
    <section className="bg-white rounded-2xl border border-background-200 p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
          <i className="ri-lock-password-line text-xl"></i>
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground-950">비밀번호 변경</h2>
          <p className="text-sm text-foreground-500">현재 비밀번호를 입력하고 새 비밀번호로 변경하세요</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="현재 비밀번호">
          <input
            type="password"
            name="current_password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
            placeholder="현재 비밀번호"
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="새 비밀번호">
            <input
              type="password"
              name="new_password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
              placeholder="6자 이상"
            />
          </Field>
          <Field label="새 비밀번호 확인">
            <input
              type="password"
              name="confirm_password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
              placeholder="다시 입력"
            />
          </Field>
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        {message && (
          <div className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
            {message}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="whitespace-nowrap bg-accent-500 hover:bg-accent-600 text-white font-semibold px-6 py-2.5 rounded-md transition-colors cursor-pointer disabled:opacity-60 text-sm"
        >
          {loading ? '변경 중...' : '비밀번호 변경'}
        </button>
      </form>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-foreground-700 mb-1.5">{label}</span>
      {children}
    </label>
  );
}